import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  MessageSquare,
  Star,
  ThumbsUp,
  ThumbsDown,
  Download,
  RefreshCw,
  Eye,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import { usePagination } from "@/hooks/usePagination";
import { useDebounce } from "@/hooks/useDebounce";
import {
  fetchUserBookFeedback,
  fetchUserBookFeedbackStats,
  type UserBookFeedback,
  type FeedbackStats,
} from "@/api/userBookFeedback";

const StarRating = ({ rating }: { rating: number | null }) => {
  if (rating === null) return <span className="text-gray-400">N/A</span>;

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
          }`}
        />
      ))}
      <span className="ml-1 text-sm font-medium">{rating}</span>
    </div>
  );
};

const UserBookFeedback = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [recommendationFilter, setRecommendationFilter] = useState("all");
  const [selectedFeedback, setSelectedFeedback] =
    useState<UserBookFeedback | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounce(searchTerm, 500);

  const {
    data: feedbackData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["user-book-feedback", ratingFilter, recommendationFilter],
    queryFn: () =>
      fetchUserBookFeedback(
        { page: 1, pageSize: 10000 },
        {
          rating: ratingFilter,
          recommendation: recommendationFilter,
        }
      ),
  });

  const filteredFeedback = useMemo(() => {
    const rows = feedbackData?.data || [];
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return rows;

    const includes = (value?: string | null) =>
      (value || "").toLowerCase().includes(term);

    return rows.filter((f) => {
      const name = f.profiles?.name || "";
      const email = f.profiles?.email || "";
      const title = f.books?.title || "";
      const authors = (f.books?.authors || [])
        .map((a) => a?.name || "")
        .join(", ");
      const review = f.optional_review || "";
      return (
        includes(name) ||
        includes(email) ||
        includes(title) ||
        includes(authors) ||
        includes(review)
      );
    });
  }, [feedbackData?.data, debouncedSearch]);

  const pagedFeedback = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredFeedback.slice(start, end);
  }, [filteredFeedback, currentPage, pageSize]);

  const { data: stats } = useQuery({
    queryKey: ["user-book-feedback-stats"],
    queryFn: fetchUserBookFeedbackStats,
  });

  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: filteredFeedback.length || 0,
    itemsPerPage: pageSize,
    currentPage,
  });

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleExportCSV = () => {
    if (!filteredFeedback || filteredFeedback.length === 0) {
      toast.error("No data to export");
      return;
    }

    const csvData = filteredFeedback.map((feedback) => ({
      "User Email": feedback.profiles?.email || "N/A",
      "User Name": feedback.profiles?.name || "N/A",
      "Book Title": feedback.books?.title || "N/A",
      "Book Author":
        feedback.books?.authors?.map((author) => author.name).join(", ") ||
        "N/A",
      "Content Rating": feedback.content_rating || "N/A",
      "Audio Rating": feedback.audio_rating || "N/A",
      "Overall Rating": feedback.overall_rating || "N/A",
      "Would Recommend":
        feedback.would_recommend === null
          ? "N/A"
          : feedback.would_recommend
          ? "Yes"
          : "No",
      "Completion %": feedback.completion_percentage
        ? `${feedback.completion_percentage}%`
        : "N/A",
      Review: feedback.optional_review || "No review",
      "Submitted Date": format(
        new Date(feedback.created_at),
        "yyyy-MM-dd HH:mm:ss"
      ),
    }));

    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        headers
          .map(
            (header) =>
              `"${String(row[header as keyof typeof row]).replace(/"/g, '""')}"`
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `user-book-feedback-${format(new Date(), "yyyy-MM-dd")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("CSV exported successfully");
  };

  const handleViewDetails = (feedback: UserBookFeedback) => {
    setSelectedFeedback(feedback);
    setIsDialogOpen(true);
  };

  return (
    <AdminLayout title="User Book Feedback">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Feedback
              </CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.totalFeedback || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Month</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.monthlyFeedback || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Average Rating
              </CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.averageRating || 0}/5
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Recommendation Rate
              </CardTitle>
              <ThumbsUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.recommendationRate || 0}%
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search by user, book title, or author..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rating">Overall Rating</Label>
                <Select
                  value={ratingFilter}
                  onValueChange={(val) => {
                    setRatingFilter(val);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All ratings" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ratings</SelectItem>
                    <SelectItem value="5">5 stars</SelectItem>
                    <SelectItem value="4">4 stars</SelectItem>
                    <SelectItem value="3">3 stars</SelectItem>
                    <SelectItem value="2">2 stars</SelectItem>
                    <SelectItem value="1">1 star</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recommendation">Recommendation</Label>
                <Select
                  value={recommendationFilter}
                  onValueChange={(val) => {
                    setRecommendationFilter(val);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All recommendations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="yes">Would recommend</SelectItem>
                    <SelectItem value="no">Would not recommend</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end gap-2">
                <Button onClick={() => refetch()} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button onClick={handleExportCSV} variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feedback Table */}
        <Card>
          <CardHeader>
            <CardTitle>User Book Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Book</TableHead>
                    <TableHead>Content Rating</TableHead>
                    <TableHead>Audio Rating</TableHead>
                    <TableHead>Overall Rating</TableHead>
                    <TableHead>Recommendation</TableHead>
                    <TableHead>Completion</TableHead>
                    <TableHead>Review</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedFeedback.map((feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {feedback.profiles?.name || "N/A"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {feedback.profiles?.email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {feedback.books?.title}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {feedback.books?.authors
                              ?.map((author) => author.name)
                              .join(", ") || "N/A"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StarRating rating={feedback.content_rating} />
                      </TableCell>
                      <TableCell>
                        <StarRating rating={feedback.audio_rating} />
                      </TableCell>
                      <TableCell>
                        <StarRating rating={feedback.overall_rating} />
                      </TableCell>
                      <TableCell>
                        {feedback.would_recommend === null ? (
                          <Badge variant="secondary">N/A</Badge>
                        ) : feedback.would_recommend ? (
                          <Badge
                            variant="default"
                            className="bg-green-100 text-green-800"
                          >
                            <ThumbsUp className="w-3 h-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <ThumbsDown className="w-3 h-3 mr-1" />
                            No
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {feedback.completion_percentage ? (
                          <div className="text-sm">
                            {feedback.completion_percentage}%
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {feedback.optional_review ? (
                          <div className="text-sm max-w-[320px] truncate">
                            {feedback.optional_review}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(feedback.created_at), "MMM dd, yyyy")}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(feedback)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {filteredFeedback &&
              filteredFeedback.length === 0 &&
              !isLoading && (
                <div className="text-center py-8 text-muted-foreground">
                  No feedback found matching your criteria.
                </div>
              )}

            {filteredFeedback &&
              Math.ceil(filteredFeedback.length / pageSize) > 1 && (
                <div className="flex items-center justify-between px-4 py-4 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    Showing{" "}
                    <span className="font-medium">
                      {filteredFeedback.length === 0
                        ? 0
                        : (currentPage - 1) * pageSize + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(
                        currentPage * pageSize,
                        filteredFeedback.length
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">
                      {filteredFeedback.length}
                    </span>{" "}
                    feedback
                  </div>
                  <div className="flex items-center gap-4">
                    {totalPages > 0 && (
                      <div className="flex items-center">
                        <Button
                          className="mr-1 px-2"
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(1)}
                          disabled={!canGoPrev}
                        >
                          First
                        </Button>

                        <Button
                          className="mr-1 px-2"
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(currentPage - 1)}
                          disabled={!canGoPrev}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>

                        {pageNumbers.map((pageNum) => (
                          <Button
                            key={pageNum}
                            className="mx-0.5 w-8 h-8 p-0"
                            variant={
                              pageNum === currentPage ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => goToPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        ))}

                        <Button
                          className="ml-1 px-2"
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(currentPage + 1)}
                          disabled={!canGoNext}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>

                        <Button
                          className="ml-1 px-2"
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(totalPages)}
                          disabled={!canGoNext}
                        >
                          Last
                        </Button>
                      </div>
                    )}

                    <select
                      className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {[10, 20, 30, 50].map((size) => (
                        <option key={size} value={size}>
                          {size} per page
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      </div>

      {/* Feedback Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Feedback Details</DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <ScrollArea className="max-h-[600px]">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">User Information</h4>
                    <div className="space-y-1 text-sm">
                      <div>
                        <strong>Name:</strong>{" "}
                        {selectedFeedback.profiles?.name || "N/A"}
                      </div>
                      <div>
                        <strong>Email:</strong>{" "}
                        {selectedFeedback.profiles?.email}
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Book Information</h4>
                    <div className="space-y-1 text-sm">
                      <div>
                        <strong>Title:</strong> {selectedFeedback.books?.title}
                      </div>
                      <div>
                        <strong>Author:</strong>{" "}
                        {selectedFeedback.books?.authors
                          ?.map((author) => author.name)
                          .join(", ") || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold mb-2">Ratings</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">
                        Content Rating
                      </div>
                      <StarRating rating={selectedFeedback.content_rating} />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">
                        Audio Rating
                      </div>
                      <StarRating rating={selectedFeedback.audio_rating} />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">
                        Overall Rating
                      </div>
                      <StarRating rating={selectedFeedback.overall_rating} />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Would Recommend
                    </div>
                    <div className="mt-1">
                      {selectedFeedback.would_recommend === null ? (
                        <Badge variant="secondary">Not specified</Badge>
                      ) : selectedFeedback.would_recommend ? (
                        <Badge
                          variant="default"
                          className="bg-green-100 text-green-800"
                        >
                          <ThumbsUp className="w-3 h-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <ThumbsDown className="w-3 h-3 mr-1" />
                          No
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Completion Percentage
                    </div>
                    <div className="mt-1 text-sm">
                      {selectedFeedback.completion_percentage
                        ? `${selectedFeedback.completion_percentage}%`
                        : "Not specified"}
                    </div>
                  </div>
                </div>

                {selectedFeedback.optional_review && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-semibold mb-2">User Review</h4>
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md text-sm">
                        {selectedFeedback.optional_review}
                      </div>
                    </div>
                  </>
                )}

                <Separator />

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Submitted</div>
                    <div>
                      {format(
                        new Date(selectedFeedback.created_at),
                        "MMM dd, yyyy HH:mm"
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Last Updated</div>
                    <div>
                      {format(
                        new Date(selectedFeedback.updated_at),
                        "MMM dd, yyyy HH:mm"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UserBookFeedback;
